<template>
  <Page color="info" title="Treffen planen">
    <template slot="help">
      <h3>Bedienung</h3>
      <ul>
        <li>Wähle mehrere Personen aus.</li>
        <li>Wähle den gewünschten Tag.</li>
        <li>Vergleiche den Unterricht.</li>
        <li>Schreib eine E-Mail.</li>
      </ul>
    </template>
    <RegisterSettings v-model="settings" :type="type">
      <div class="d-flex align-center">
        <PersonPicker
          @input="addPersons"
          color="primary"
          outlined
          class="mr-2"
          text="personen hinzufügen"
        ></PersonPicker>
        <div class="mx-1"></div>
        <EmailChip
          :disabled="!people.length"
          title="E-Mail"
          :value="
            joinEmails(people.map((p) => p.emailSchool)) +
            '?subject=' +
            formatDate(settings.focus, false, true)
          "
        ></EmailChip>
      </div>
    </RegisterSettings>
    <v-card :loading="loading">
      <v-system-bar><DateValue :value="settings.focus" long /></v-system-bar>
      <v-calendar
        v-bind="$attrs"
        ref="calendar"
        first-interval="7"
        interval-count="14"
        v-model="settings.focus"
        category-show-all
        locale="de-CH"
        :type="type"
        @click:event="showEvent"
        :categories="
          settings.showEvents
            ? ['Termine', ...people.map((p) => '' + p.id)]
            : people.map((p) => '' + p.id)
        "
        :events="events"
        :locale-first-day-of-year="4"
        @change="change"
      >
        <template v-slot:category="{ category }"
          ><div class="text-center mb-2">
            <span v-if="category === 'Termine'">Termine</span>
            <PersonChip
              v-else
              hideExt
              close
              @click:close="remove(people.find((p) => '' + p.id === category))"
              :value="people.find((p) => '' + p.id === category)"
            ></PersonChip></div
        ></template>

        <template v-slot:event="{ event, eventParsed, eventSummary }">
          <template v-if="event.event">
            <v-badge
              inline
              bordered
              left
              dot
              :color="eventColor(event.event)"
              v-if="event.event && eventColor(event.event)"
            >
              <component
                :is="{ render: eventSummary }"
                :class="event.canceled ? 'canceled' : ''"
                class="ma-1"
              ></component>
            </v-badge>

            <component
              :is="{ render: eventSummary }"
              v-else
              class="mx-1"
            ></component>
          </template>

          <template v-else-if="event.lesson">
            <v-tooltip
              top
              v-for="note in event.lesson.notes"
              :key="'note' + note.id"
            >
              <template v-slot:activator="{ on, attrs }">
                <div v-bind="attrs" v-on="on">
                  <v-icon small class="float-right mr-1" dark>
                    {{ noteIcon(note) }}
                  </v-icon>
                  <v-icon
                    small
                    class="float-right mr-1"
                    dark
                    v-if="note.smartLearn"
                  >
                    mdi-laptop
                  </v-icon>
                </div>
              </template>
              <span
                >{{ note.type.description }}: {{ note.title }}
                {{ note.smartLearn ? "(Smartlearn)" : "" }}</span
              >
            </v-tooltip>

            <v-tooltip top v-if="event.lesson.absence">
              <template v-slot:activator="{ on, attrs }">
                <v-icon
                  v-bind="attrs"
                  v-on="on"
                  small
                  class="float-right mr-1"
                  dark
                >
                  mdi-bed
                </v-icon>
              </template>
              <span>Sie waren in dieser Lektion abwesend!</span>
            </v-tooltip>
            <div>
              <v-badge
                inline
                bordered
                dot
                left
                :color="lessonStatusColor(event.lesson.status)"
              >
                <v-tooltip
                  top
                  :disabled="
                    event.lesson.status &&
                    event.lesson.status.code == 'asPlanned'
                  "
                >
                  <template v-slot:activator="{ on, attrs }">
                    <div
                      v-bind="attrs"
                      v-on="on"
                      :class="{ canceled: event.canceled }"
                    >
                      <strong
                        >{{ event.name
                        }}<span
                          v-if="
                            event.lesson.rooms.length > 0 ||
                            event.lesson.reservations.length > 0
                          "
                        >
                          ({{
                            [
                              ...event.lesson.rooms.map((el) => el.code),
                              ...event.lesson.reservations.map(
                                (el) => "+" + el.room.code
                              ),
                            ].join(", ")
                          }})</span
                        ></strong
                      >
                    </div> </template
                  ><span>{{
                    event.lesson.status
                      ? event.lesson.status.description
                      : "Spezial"
                  }}</span>
                </v-tooltip>
              </v-badge>
              <div style="margin-top: -2px; margin-left: 16px">
                {{ eventParsed.start.time }} –
                {{ eventParsed.end.time }}
              </div>
            </div>
          </template>

          <div
            v-else
            :class="{ canceled: event.canceled }"
            style="padding-left: 3px"
          >
            <strong>{{ event.name }} </strong><br />
            <div style="margin-top: -2px">
              {{ eventParsed.start.time }} –
              {{ eventParsed.end.time }}
            </div>
          </div>
        </template>
      </v-calendar>
      <v-bottom-sheet v-model="dialog" width="500">
        <LessonSheet
          v-if="selectedEvent && selectedEvent.lesson"
          :day="selectedEvent.day"
          :value="selectedEvent.lesson"
          @close="dialog = false"
          @updated="fetchData"
        ></LessonSheet>
        <EventSheet
          v-if="selectedEvent && selectedEvent.event"
          :value="selectedEvent.event"
          @close="dialog = false"
        ></EventSheet>
        <HolidaySheet
          v-if="selectedEvent && selectedEvent.holiday"
          :value="selectedEvent.holiday"
          @close="dialog = false"
        ></HolidaySheet>
      </v-bottom-sheet>
    </v-card>
  </Page>
</template>
<script>
import { defineComponent } from "vue";
import { findMonday, formatDate } from "common/utils/date.js";
import { lessonStatusColor, noteIcon } from "common/utils/icons.js";

import DateInput from "common/components/DateInput.vue";
import DateValue from "common/components/DateValue.vue";
import EmailChip from "common/components/EmailChip.vue";
import PersonChip from "common/components/PersonChip.vue";
import PersonPicker from "common/components/Finance/PersonPicker.vue";
import RegisterSettings from "common/components/Register/RegisterSettings.vue";

import EventSheet from "common/components/EventSheet.vue";
import HolidaySheet from "common/components/HolidaySheet.vue";
import LessonSheet from "common/components/Register/LessonSheet.vue";

import { joinEmails } from "common/utils/helper.js";

export default defineComponent({
  components: {
    DateInput,
    DateValue,
    EventSheet,
    EmailChip,
    HolidaySheet,
    LessonSheet,
    PersonChip,
    PersonPicker,
    RegisterSettings,
  },
  props: { id: [] },
  data() {
    return {
      abortController: null,
      dialog: false,
      start: "",
      end: "",
      events: [],
      loading: false,
      people: [],
      selectedEvent: {},
      type: "category",
      settings: {
        focus: "",
        showEvents: false,
      },
    };
  },
  computed: {
    cal() {
      return this.ready ? this.$refs.calendar : null;
    },
  },
  watch: {
    "settings.focus": async function () {
      await this.$nextTick();
      this.fetchData();
    },

    people() {
      localStorage.setItem(
        "calendarIds",
        JSON.stringify(this.people.map((el) => el.id))
      );
      this.fetchData();
    },
  },
  methods: {
    joinEmails,
    formatDate,
    findMonday,
    lessonStatusColor,
    noteIcon,
    eventColor(event) {
      if (event.canceled) {
        return "danger";
      }
      if (event.current) {
        return "primary";
      }
      if (event.changed) {
        return "error";
      }
      return "";
    },
    async addPersons(persons) {
      for (const person of persons) {
        if (!this.people.some((el) => el.id === person.id)) {
          this.people.push(person);
        }
      }
    },
    color(index) {
      return this.colors[index % this.colors.length];
    },
    dark(index) {
      return this.darks[index % this.darks.length];
    },
    async fetchData() {
      if (this.people.length === 0) {
        return;
      }
      this.loading = true;
      if (this.abortController) {
        this.abortController.abort();
      }
      this.abortController = new AbortController();
      this.events = [];
      const newEvents = [];

      for (const person of this.people) {
        const type = person.type.code === "student" ? "student" : "teacher";
        const register = await this.apiList({
          resource: "register/register",
          query: `${type}=${person.id}&startDate=${this.start}&endDate=${this.end}`,
          signal: this.abortController.signal,
        });
        if (register && register.days) {
          for (const day of register.days) {
            if (day.holiday) {
              const eventStart = new Date(day.holiday.startDate);
              const eventEnd = new Date(day.holiday.endDate);

              newEvents.push({
                name: day.holiday.name,
                category: "" + person.id,
                start: eventStart,
                end: eventEnd,
                startDate: day.holiday.startDate,
                endDate: day.holiday.endDate,
                day: day,
                color: "warning",
                timed: false,
                holiday: day.holiday,
              });
            }
            if (day.events) {
              for (const event of day.events) {
                if (
                  newEvents.some((el) => el.event && el.event.id === event.id)
                ) {
                  continue;
                }
                const eventStart = event.startTime
                  ? new Date(event.startDate + "T" + event.startTime)
                  : new Date(event.startDate);
                const eventEnd = event.endTime
                  ? new Date(event.endDate + "T" + event.endTime)
                  : new Date(event.endDate);

                newEvents.push({
                  id: event.id,
                  name: event.description,
                  category: "Termine",
                  start: eventStart,
                  end: eventEnd,
                  startDate: event.startDate,
                  endDate: event.endDate,
                  day: day,
                  color: event.past ? "success" : "success darken-2",
                  timed: event.startTime && event.endTime,
                  event: event,
                  canceled: event.canceled,
                });
              }
            }
            if (day.lessons) {
              for (const lesson of day.lessons) {
                const eventStart = new Date(day.date + "T" + lesson.startTime);
                const eventEnd = new Date(day.date + "T" + lesson.endTime);
                newEvents.push({
                  name: lesson.course.code,
                  start: eventStart,
                  end: eventEnd,
                  timed: true,
                  category: "" + person.id,
                  lesson: lesson,
                  day: day,
                });
              }
            }

            if (day.canceledBy) {
              const event = day.canceledBy;
              const eventStart = event.startTime
                ? new Date(event.startDate + "T" + event.startTime)
                : new Date(event.startDate);
              const eventEnd = event.endTime
                ? new Date(event.endDate + "T" + event.endTime)
                : new Date(event.endDate);

              newEvents.push({
                name: event.description,
                category: "Termine",
                start: eventStart,
                end: eventEnd,
                startDate: event.startDate,
                endDate: event.endDate,
                day: day,
                color: "success",
                timed: event.startTime && event.endTime,
                event: event,
                canceled: event.canceled,
              });
            }
          }
        }
      }

      this.events = newEvents;

      this.$router
        .replace({
          query: { id: this.people.map((el) => el.id) },
        })
        .catch((error) => {
          if (error.name != "NavigationDuplicated") {
            throw error;
          }
        });

      this.loading = false;
    },
    showEvent({ nativeEvent, event }) {
      this.selectedEvent = event;
      this.dialog = true;
      nativeEvent.stopPropagation();
    },
    change(event) {
      this.start = event.start.date;
      this.end = event.end.date;
    },
    remove(person) {
      this.people = this.people.filter((el) => el.id !== person.id);
    },
  },
  async created() {
    let ids = [];
    if (this.$route.query.id) {
      const queryId = this.$route.query.id;
      if (Array.isArray(queryId)) {
        ids = queryId;
      } else {
        ids = [queryId];
      }
    } else {
      ids = localStorage.getItem("calendarIds")
        ? JSON.parse(localStorage.getItem("calendarIds"))
        : [];
    }
    const peopleNew = [];
    for (const id of ids) {
      const person = await this.apiGet({
        resource: "person/person",
        id: id,
      });
      peopleNew.push(person);
    }
    this.people = peopleNew;
  },
});
</script>

<style scoped lang="scss">
.canceled,
.canceled .v-event-summary {
  text-decoration-line: line-through;
}
</style>
